import React, { useMemo, useRef, useState } from 'react';
import { motion } from 'motion/react';
import { Calendar, MapPin, Clock, Volume2, VolumeX, Navigation } from 'lucide-react';
import { registry } from '../registry';
import type { InvitationData, TemplateMetadata } from '../types';
import {
  extractCoupleNames,
  parseFlexibleDate,
} from '@/src/infrastructure/lib/weddingDataHelper';
import { safeLinkUrl } from '@/src/modules/renderer/url-safety';
import { DiyaIcon, ToranBorder } from '../_shared/SacredArtifacts';
import { DiyaEmbers, IncenseSmoke } from '../_shared/WowEffects';
import { SpinningRangoli, TempleBellRack } from '../_shared/SignatureEffects';
import {
  pickWeddingStr,
  resolveCeremonySlots,
  resolveCoupleGallery,
  resolveCouplePhotos,
  resolveExpMeta,
} from '../_shared/octoberWeddingFields';
import '../_shared/wow-effects.css';
import './theme.css';

const META: TemplateMetadata = {
  id: 'exp-vedic-mandir-divine',
  name: 'Vedic Mandir Divine',
  occasionType: 'WEDDING',
  description:
    'Sacred temple wedding microsite: couple hero portrait, soft mandir atmosphere, multi-ceremony path, RSVP and maps.',
  isPremium: true,
  tags: ['wedding', 'temple', 'vedic', 'sacred', 'saffron', 'multi-ceremony', 'premium', 'hindu'],
  colorScheme: { primary: '#8B1E1E', accent: '#D4AF37', bg: '#1A0A0A' },
};

/** Original generated mandir night — no stock cars / urban scenes */
const TEMPLE_ATMOSPHERE = '/assets/october-templates/vedic-temple-night.jpg';

const CEREMONY_SLOTS = ['Haldi', 'Mehendi', 'Sangeet', 'Vivah', 'Reception'] as const;

export function VedicMandirDivine({ data }: { data: InvitationData }) {
  const [muted, setMuted] = useState(true);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const expMeta = resolveExpMeta(data);
  const wedding = data.customData?.wedding;
  const extracted = extractCoupleNames(data.eventName);

  const groomName = pickWeddingStr(
    wedding?.groom?.fullName,
    wedding?.groom?.nickname,
    expMeta.groomName,
    expMeta.secondaryName,
    extracted?.groom,
  );
  const brideName = pickWeddingStr(
    wedding?.bride?.fullName,
    wedding?.bride?.nickname,
    expMeta.brideName,
    expMeta.primaryName,
    extracted?.bride,
  );
  const coupleTitle =
    groomName && brideName ? `${groomName} & ${brideName}` : groomName || brideName || data.eventName || '';

  const parsedDate = parseFlexibleDate(data.eventDate);
  const eventDateLabel = parsedDate
    ? parsedDate.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
    : pickWeddingStr(data.eventDate, expMeta.weddingDate as string, expMeta.eventDate as string);

  const venue = pickWeddingStr(data.eventVenue, wedding?.weddingEvents?.[0]?.venue, expMeta.venue as string);
  const hostName = pickWeddingStr(data.hostName, expMeta.hostName as string);
  const tagline = pickWeddingStr(
    data.customMessage,
    expMeta.tagline as string,
    'With the blessings of elders and the light of a thousand diyas, we invite you to our sacred vivah.',
  );

  const { bridePhoto, groomPhoto, couplePhoto, heroPortrait } = resolveCouplePhotos(data);
  const gallery = useMemo(() => resolveCoupleGallery(data), [data]);
  const ceremonies = useMemo(
    () => resolveCeremonySlots(data, CEREMONY_SLOTS, venue),
    [data, venue],
  );
  const customVideoUrl = safeLinkUrl(
    pickWeddingStr(
      (data.customData as { customVideoUrl?: string; preWeddingVideoUrl?: string } | null)?.customVideoUrl,
      (data.customData as { preWeddingVideoUrl?: string } | null)?.preWeddingVideoUrl,
      expMeta.customVideoUrl as string,
    ),
  );
  const rsvpHref = safeLinkUrl(data.rsvpLink);

  const mapsUrl = venue
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(venue)}`
    : '';

  const toggleAudio = () => {
    const el = audioRef.current;
    if (!el) {
      setMuted((m) => !m);
      return;
    }
    if (muted) {
      void el.play().catch(() => undefined);
      setMuted(false);
    } else {
      el.pause();
      setMuted(true);
    }
  };

  return (
    <div className="vmd-root" data-template="exp-vedic-mandir-divine">
      <audio
        ref={audioRef}
        loop
        preload="none"
        src="https://cdn.pixabay.com/download/audio/2022/03/24/audio_c8c8a73467.mp3?filename=temple-bells-ambient-110544.mp3"
      />
      <button type="button" className="vmd-audio" onClick={toggleAudio} aria-label={muted ? 'Play ambient audio' : 'Mute audio'}>
        {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
      </button>

      {/* ── Hero: atmosphere photo + temple craft ── */}
      <section className="vmd-hero">
        <div
          className="vmd-hero-bg"
          style={{ backgroundImage: `url(${TEMPLE_ATMOSPHERE})` }}
          aria-hidden
        />
        <div className="vmd-hero-veil" aria-hidden />
        {/* Vedic signature: rangoli spin + tap bells + diya/incense (NOT bubbles/sand/foil) */}
        <SpinningRangoli />
        <DiyaEmbers count={18} />
        <IncenseSmoke count={8} />
        <ToranBorder className="vmd-toran" />

        <TempleBellRack />

        <div className="vmd-diya-row" aria-hidden>
          <DiyaIcon size={44} />
          <DiyaIcon size={58} />
          <DiyaIcon size={44} />
        </div>

        <p className="vmd-eyebrow">॥ शुभ विवाह ॥</p>
        <motion.h1
          className="vmd-names"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          {coupleTitle || 'Sacred Union'}
        </motion.h1>
        <p className="vmd-event">{data.eventName || 'Wedding Celebration'}</p>

        {heroPortrait ? (
          <div className="vmd-couple-orb">
            <img src={heroPortrait} alt={coupleTitle || 'The couple'} />
          </div>
        ) : (
          <div className="vmd-couple-orb vmd-couple-orb--empty" aria-label="Couple photo placeholder">
            <span>Your portrait</span>
            <small>Upload in Details → Media</small>
          </div>
        )}

        <div className="vmd-meta-row">
          {eventDateLabel ? (
            <span>
              <Calendar size={14} /> {eventDateLabel}
            </span>
          ) : null}
          {data.eventTime ? (
            <span>
              <Clock size={14} /> {data.eventTime}
            </span>
          ) : null}
          {venue ? (
            <span>
              <MapPin size={14} /> {venue}
            </span>
          ) : null}
        </div>
        {hostName ? <p className="vmd-host">With blessings from {hostName}</p> : null}

        <div className="vmd-hero-floor" aria-hidden />
      </section>

      <section className="vmd-photos">
        <h2 className="vmd-section-title">The couple</h2>
        <div className="vmd-photo-grid">
          <figure className={`vmd-avatar${groomPhoto ? '' : ' vmd-avatar--empty'}`}>
            {groomPhoto ? <img src={groomPhoto} alt={groomName || 'Groom'} /> : <span>Groom photo</span>}
            <figcaption>{groomName || 'Groom'}</figcaption>
          </figure>
          <figure className={`vmd-avatar vmd-avatar--couple${couplePhoto ? '' : ' vmd-avatar--empty'}`}>
            {couplePhoto ? (
              <img src={couplePhoto} alt={coupleTitle || 'Couple'} />
            ) : (
              <span>Couple photo</span>
            )}
            <figcaption>Together</figcaption>
          </figure>
          <figure className={`vmd-avatar${bridePhoto ? '' : ' vmd-avatar--empty'}`}>
            {bridePhoto ? <img src={bridePhoto} alt={brideName || 'Bride'} /> : <span>Bride photo</span>}
            <figcaption>{brideName || 'Bride'}</figcaption>
          </figure>
        </div>
      </section>

      {gallery.length > 0 ? (
        <section className="vmd-gallery" aria-label="Couple photo gallery">
          <h2 className="vmd-section-title">Gallery</h2>
          <div className="vmd-gallery-grid">
            {gallery.map((item) => (
              <figure key={item.key} className="vmd-gallery-item">
                {item.isVideo ? (
                  <video src={item.url} controls playsInline preload="metadata" />
                ) : (
                  <img src={item.url} alt={item.caption || 'Memory'} loading="lazy" />
                )}
                {item.caption ? <figcaption>{item.caption}</figcaption> : null}
              </figure>
            ))}
          </div>
        </section>
      ) : null}

      <section className="vmd-blessing">
        <DiyaIcon size={36} className="vmd-blessing-diya" />
        <p>{tagline}</p>
      </section>

      <section className="vmd-schedule">
        <h2 className="vmd-section-title">Sacred ceremonies</h2>
        <ol className="vmd-path">
          {ceremonies.map((c, i) => (
            <li key={c.key} className="vmd-path-item">
              <span className="vmd-path-marker" aria-hidden>
                <DiyaIcon size={28} />
                {i < ceremonies.length - 1 ? <span className="vmd-path-line" /> : null}
              </span>
              <div className={`vmd-path-card${c.filled ? '' : ' vmd-path-card--placeholder'}`}>
                {c.coverImageUrl ? (
                  <img className="vmd-path-cover" src={c.coverImageUrl} alt="" loading="lazy" />
                ) : null}
                <h3>{c.label}</h3>
                {c.filled ? (
                  <>
                    {c.date ? <p>{c.date}</p> : null}
                    {c.time ? <p>{c.time}</p> : null}
                    {c.venue ? <p className="vmd-path-venue">{c.venue}</p> : null}
                  </>
                ) : (
                  <p className="vmd-path-hint">Date &amp; venue TBD</p>
                )}
              </div>
            </li>
          ))}
        </ol>
      </section>

      {customVideoUrl ? (
        <section className="vmd-video">
          <h2 className="vmd-section-title">Our story</h2>
          <video controls playsInline src={customVideoUrl} />
        </section>
      ) : null}

      {mapsUrl ? (
        <section className="vmd-map">
          <a href={mapsUrl} target="_blank" rel="noreferrer">
            <Navigation size={16} /> Open in Google Maps
          </a>
        </section>
      ) : null}

      <section id="vmd-rsvp" className="vmd-rsvp">
        <ToranBorder className="vmd-toran vmd-toran--sm" />
        <h2>Bless us with your presence</h2>
        <p>Kindly confirm your attendance. We look forward to celebrating with you.</p>
        {rsvpHref ? (
          <a className="vmd-cta" href={rsvpHref}>
            Open RSVP
          </a>
        ) : (
          <p className="vmd-rsvp-hint">RSVP will appear when this invitation is published.</p>
        )}
      </section>
    </div>
  );
}

registry.register({ metadata: META, component: VedicMandirDivine });
